<script lang="ts">
import StudySlide from '../StudySlide.svelte'
import StudySlideContent from '../StudySlideContent.svelte'

/** Matches existing cover slides (GLP-1 / ADC). */
export const STUDY_COVER_BACKGROUND =
  'radial-gradient(ellipse 80% 55% at 50% 0%,rgba(17,160,165,.18) 0%,transparent 58%),radial-gradient(ellipse 40% 35% at 92% 85%,rgba(7,69,83,.10) 0%,transparent 55%),var(--slide-bg-solid, #030b0a)'

/** Top display lines: line 1, emphasized middle line, optional third line (see GLP-1 cover). */
export interface StudyCoverTagline {
  line1: string
  line2Emphasis: string
  line3?: string
}

interface Props {
  id: string
  active?: boolean
  tagline: StudyCoverTagline
  /** Large italic serif title (e.g. therapy or topic name). */
  title: string
  /**
   * Font size in `rem` for the title (GLP-1 uses 4; longer titles e.g. ADC use 3.5).
   * @default 4
   */
  titleFontRem?: number
  /** One line under the title (often dynamic stats). */
  subtitle: string
  /** Supporting paragraph below the spacer. */
  body: string
  /** Optional second supporting paragraph, rendered under `body` with a small gap. */
  body2?: string
  /** @default true */
  showKeyboardHint?: boolean
  /** @default 'Press arrow keys, space, or Enter to advance' */
  keyboardHint?: string
}

let {
  id,
  active = true,
  tagline,
  title,
  titleFontRem = 4,
  subtitle,
  body,
  body2,
  showKeyboardHint = true,
  keyboardHint = 'Press arrow keys, space, or Enter to advance',
}: Props = $props()
</script>

<StudySlide {id} {active} background={STUDY_COVER_BACKGROUND} slideClass="slide--center">
  <StudySlideContent
    maxWidth="800px"
    textAlign="center"
    contentClass="cover-inn study-cover-slide__inn"
    style="padding-top:4rem;"
  >
    <div class="disp study-cover-slide__tagline-margin">
      {tagline.line1}<br />
      <em>{tagline.line2Emphasis}</em>
      {#if tagline.line3}
        <br />{tagline.line3}
      {/if}
    </div>
    <p class="study-cover-slide__title cover-title" style:font-size={`${titleFontRem}rem`}>
      {title}
    </p>
    <p class="study-cover-slide__subtitle">
      {subtitle}
    </p>
    <div class="study-cover-slide__spacer cover-spacer"></div>
    <p class="study-cover-slide__body">
      {body}
    </p>
    {#if body2}
      <p class="study-cover-slide__body study-cover-slide__body2">
        {body2}
      </p>
    {/if}
    {#if showKeyboardHint}
      <p class="mn study-cover-slide__hint" data-hide-in-pdf>
        {keyboardHint}
      </p>
    {/if}
  </StudySlideContent>
</StudySlide>

<style>
.study-cover-slide__tagline-margin {
  margin-bottom: 2.5rem;
}
.study-cover-slide__title {
  max-width: 600px;
  margin: 0 auto 0.6rem;
  font-family: 'DM Serif Display', serif;
  font-style: italic;
  color: var(--slide-fg, #fff);
  letter-spacing: -0.02em;
  line-height: 1;
}
.study-cover-slide__subtitle {
  max-width: 600px;
  margin: 0 auto 0;
  font-size: 1.25rem;
  line-height: 1.8;
  color: var(--cover-sub, rgba(255, 255, 255, 0.85));
  font-weight: 600;
}
.study-cover-slide__spacer {
  height: 2.5rem;
}
.study-cover-slide__body {
  max-width: 520px;
  margin: 0 auto;
  font-size: 1.05rem;
  line-height: 1.6;
  color: var(--g);
}
.study-cover-slide__body2 {
  margin-top: 0.9rem;
}
.study-cover-slide__hint {
  margin-top: 2rem;
  opacity: 0.85;
  font-size: 0.82rem;
}

/* Cover: scale oversized title and tighten vertical rhythm on narrow viewports */
@media (max-width: 640px) {
  :global(.study-cover-slide__inn) {
    padding-top: 1.75rem !important;
  }
  .study-cover-slide__title {
    font-size: 2.2rem !important;
  }
  .study-cover-slide__spacer {
    height: 1rem !important;
  }
}
@media (max-width: 430px) {
  :global(.study-cover-slide__inn) {
    padding-top: 1.25rem !important;
  }
  .study-cover-slide__title {
    font-size: 1.85rem !important;
  }
  .study-cover-slide__spacer {
    height: 0.5rem !important;
  }
}
</style>
